/* eslint-disable indent */
import React, { Fragment, useEffect, useState } from 'react';
import { useKeplerNavigate } from '../../../navigation/guards/use-kepler-navigate';
import { useAppActions, useAppState } from '../../../overmind';
import { Anchor, Button, colourString, PageHeader, SmartSelect, SmartSelectSection, Tooltip } from '@keplerco/core';
import { AssessmentSubType, AssessmentType } from '../../../enums/assessment-type';
import { PagePath } from '../../../navigation/navigation.enums';
import { AssessmentAssigneeType, FetchType } from '../../../enums';
import { AssessmentFOPWidget } from './assessment-fop.widget';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { AssessmentDetailsWidget } from '../widgets/details/assessment-details.widget';
import { AssessmentPreferencesWidget } from '../widgets/preferences/assessment-preferences.widget';
import { AssessmentEntitiesWidget } from '../widgets/entities/assessment-entities.widget';
import { AssessmentTagsWidget } from '../widgets/tags/assessment-tags.widget.step';
import { useParams } from 'react-router';
import { AssessmentRequest, AssessmentAssigneesResponse } from '../../../overmind/actions';
import { AssessmentStatus } from '../../../enums/assessment-status';

enum Steps {
  Details = 'Details',
  Preferences = 'Preferences',
  Entities = 'Entities',
  FieldsOfPractice = 'Fields of Practice',
  Tags = 'Tags',
}

// TODO: onChange decrease index
export function FOPAssessmentPage(): JSX.Element {
  const params = useParams();
  const keplerNavigate = useKeplerNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [activeIndex, setActiveIndex] = useState<number>(0);

  const [assignees, setAssignees] = useState<AssessmentAssigneesResponse>({
    includeLearnersWithNoDepartment: false,
    assignees: [],
    teams: [],
    departments: [],
    assigneeType: AssessmentAssigneeType.Company,
    assessmentSlug: '',
  });

  const [details, setDetails] = useState<AssessmentRequest>({
    name: '',
    companySlug: companyVariables.slug!,
    slug: '',
    assessmentType: AssessmentType.FieldsOfPractice,
    assessmentStatus: AssessmentStatus.Draft,
    keplerPointsAvailable: 0,
    description: '',
    minScore: 0,
    maxScore: 0,
    allowReassessment: false,
    forceAssessment: false,
    assessmentSubType: AssessmentSubType.FOPAssessment
  });
  const [assessmentSlug, setAssessmentSlug] = useState<string | undefined>(params.assessmentSlug);

  useEffect(() => {
    async function getDetails() {
      if (!assessmentSlug) return;
      const response = await actions.getAssessment(assessmentSlug);
      if (!!response) {
        setDetails({
          name: response.name,
          companySlug: companyVariables.slug!,
          slug: response.slug,
          assessmentType: response.assessmentType,
          assessmentStatus: response.assessmentStatus,
          keplerPointsAvailable: response.keplerPointsAvailable,
          description: response.description,
          minScore: response.minScore ?? 0,
          maxScore: response.maxScore ?? 0,
          allowReassessment: response.allowReassessment,
          forceAssessment: response.forceAssessment,
          assessmentSubType: response.assessmentSubType ?? AssessmentSubType.FOPAssessment
        });
      }
    }

    async function getPeople() {
      if (!assessmentSlug) return;

      // Get all employees
      const assignees = await actions.getAssessmentAssignees(assessmentSlug!);

      if (!!assignees) {
        setAssignees(assignees);
      }
    }

    async function getData() {
      if (!assessmentSlug) return;

      setIsLoading(true);

      await getDetails();
      await getPeople();

      setIsLoading(false);
    }

    getData();
  }, [assessmentSlug, companyVariables.slug]);

  const steps = [
    <SmartSelectSection
      key={Steps.Details}
      headerContent={
        <Fragment>
          <h3>{Steps.Details}</h3>
          <p style={{ color: colourString('text_1') }}>Name your assessment, and choose how many Kepler Points you would like to award</p>
        </Fragment>
      }
    >
      <AssessmentDetailsWidget
        isLoading={isLoading}
        details={details}
        onSaveAndContinue={details => {
          setActiveIndex(1);
          setDetails(details);
          setAssessmentSlug(details.slug);
        }}
      />
    </SmartSelectSection>,
    <SmartSelectSection
      key={Steps.Preferences}
      headerContent={
        <Fragment>
          <h3>{Steps.Preferences}</h3>
          <p style={{ color: colourString('text_1') }}>Choose your preferences for your assessment</p>
        </Fragment>
      }
    >
      <AssessmentPreferencesWidget assessmentSlug={details?.slug} assessmentType={AssessmentType.FieldsOfPractice} details={details} setDetails={setDetails} onSaveAndContinue={() => setActiveIndex(2)} />
    </SmartSelectSection>,
    <SmartSelectSection
      key={Steps.Entities}
      headerContent={
        <Fragment>
          <h3>{Steps.Entities}</h3>
          <p style={{ color: colourString('text_1') }}>Select entities for assessment</p>
        </Fragment>
      }
    >
      <AssessmentEntitiesWidget assignees={assignees} onSaveAndContinue={() => setActiveIndex(3)} />
    </SmartSelectSection>,
    <SmartSelectSection
      key={Steps.FieldsOfPractice}
      headerContent={
        <Fragment>
          <h3>{Steps.FieldsOfPractice}</h3>
          <p style={{ color: colourString('text_1') }}>Select the fields of practice for the assessment</p>
        </Fragment>
      }
    >
      <AssessmentFOPWidget details={details} onSaveAndContinue={() => setActiveIndex(4)} />
    </SmartSelectSection>,
    <SmartSelectSection
      key={Steps.Tags}
      headerContent={
        <Fragment>
          <h3>{Steps.Tags}</h3>
          <p style={{ color: colourString('text_1') }}>Assign tags to an assessment for analytics</p>
        </Fragment>
      }
    >
      <AssessmentTagsWidget assessmentSlug={details?.slug} />
    </SmartSelectSection>,
  ];

  return (
    <PageLoaderLayer path={PagePath.assessmentManagementCreateFOPAssessment}>
      <div className="wrapper stack" style={{ paddingBottom: 30, gap: 30 }}>
        <PageHeader
          breadcrumbs={
            !assessmentSlug
              ? [
                { name: 'Assessment Management', url: PagePath.assessmentManagementBase },
                { name: 'Create assessment', url: `${PagePath.assessmentManagementBase}${PagePath.assessmentManagementCreate.replace(':companySlug', companyVariables.slug!)}` },
                { name: details?.name || 'Fields of practice assessment', url: location.pathname },
              ]
              : [
                { name: 'Assessment Management', url: PagePath.assessmentManagementBase },
                { name: details?.name || 'Fields of practice assessment', url: location.pathname },
              ]
          }
          title={details?.name || 'Fields of practice assessment'}
          subtitle={!assessmentSlug ? 'Create an assessment and get skill scores for your learners' : 'Edit an assessment and get skill scores for your learners'}
          divider
        />

        <div style={{ width: '100%' }}>
          {/* TODO: fix SmartSelect so this isn't required */}
          <SmartSelect>{steps.filter((_, index) => index <= activeIndex)}</SmartSelect>
        </div>

        <footer className="card" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Anchor arrow reverse onClick={() => keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementCreate.replace(':companySlug', companyVariables.slug!)}`)}>
            Back
          </Anchor>

          <div style={{ display: 'flex', flexDirection: 'row', gap: 15 }}>
            {activeIndex === steps.length - 1 && <Anchor onClick={() => keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagement.replace(':companySlug', companyVariables.slug!)}`)}>Save draft</Anchor>}
            {activeIndex < steps.length - 1 || !details?.slug ? (
              <Tooltip content="Complete all the above steps">
                <Button type="button" disabled>
                  Activate
                </Button>
              </Tooltip>
            ) : (
              <Button
                type="button"
                onClick={async () => {
                  actions.startLoader({ path: PagePath.assessmentManagementCreateFOPAssessment, type: FetchType.Sending });
                  const response = await actions.activateAssessment(details.slug!);
                  if (response.error) return actions.stopLoader(PagePath.assessmentManagementCreateFOPAssessment);
                  keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagement.replace(':companySlug', companyVariables.slug!)}`);
                  actions.stopLoader(PagePath.assessmentManagementCreateFOPAssessment);
                }}
              >
                Activate
              </Button>
            )}
          </div>
        </footer>
      </div>
    </PageLoaderLayer>
  );
}
